/*
 *  -----------------------------------------------------------------------
 *    <copyright file="assign-modules/index.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */
import React, { useEffect, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DialogContent } from '@mui/material';

import { useBoolean, useTranslation } from 'hooks';
import { GET_SYSTEM_ORGANISATIONS, SAVE_ORGANISATION_MODULES } from 'queries/org-admin';
import { GET_ORGANISATION } from 'queries/uam';
import Constants from 'utils/constants';
import NotificationService from 'utils/notification';
import Button from 'views/components/core/buttons';
import { CustomDialog, DialogActions, DialogTitle } from 'views/components/core/dialog';
import ItemList from 'views/components/core/item-list';

import './styles.scss';

interface AssignModulesDialogInterface {
    open: boolean;
    close(action?: string): any;
    currentOrganization: any;
    modulesAvailable: any[];
}

const AssignModulesToOrganisationDialog = ({
    open,
    close,
    currentOrganization,
    modulesAvailable,
}: AssignModulesDialogInterface) => {
    const { t } = useTranslation();
    const disabledSaveButton = useBoolean(true);
    const [modulesList, setModulesList] = useState([]);
    const [selectedItems, setSelectedItems] = useState([]);
    const [loadOrganization, { data }] = useLazyQuery(GET_ORGANISATION);
    const [fullOrganisation, setFullOrganisation] = useState(null);
    const [saveOrganisationModules] = useMutation(SAVE_ORGANISATION_MODULES);

    useEffect(() => {
        if (currentOrganization) loadOrganization({ variables: { key: currentOrganization.key } });
    }, [currentOrganization]);

    useEffect(() => {
        if (data?.organisation) setFullOrganisation({ ...data.organisation });
    }, [data]);

    const onClose = (action?: string) => {
        setFullOrganisation(null);
        disabledSaveButton.setTrue();
        close(action);
    };

    const setSelectedItemsByDefault = () => {
        const selectedByDefault: { name: string; id: number; disabled?: boolean }[] = [];
        const currentModulesList = Object.keys(Constants.Modules)
            .filter((moduleKey) => moduleKey !== 'ISMP')
            .map((moduleKey) => {
                const moduleValue = (Constants.Modules as any)[moduleKey];
                let isDisabled = false;
                fullOrganisation.roles.forEach((role: any) => {
                    isDisabled =
                        !!role.rights.find(
                            (el: any) =>
                                el.moduleId === moduleValue && el.permissions > 0 && el.element?.match(/page|data/),
                        ) && fullOrganisation.modules.includes(moduleKey);
                    if (isDisabled) return;
                });
                if (fullOrganisation.modules.includes(moduleKey))
                    selectedByDefault.push({
                        name: t(`admin:${moduleKey}`),
                        id: moduleValue,
                        disabled: isDisabled,
                    });
                return {
                    name: t(`admin:${moduleKey}`),
                    id: moduleValue,
                    disabled: isDisabled,
                };
            });
        setSelectedItems(selectedByDefault);

        return currentModulesList;
    };

    useEffect(() => {
        if (fullOrganisation?.modules) setModulesList(setSelectedItemsByDefault());
    }, [fullOrganisation, modulesAvailable]);

    const successRequest = () => {
        NotificationService.showNotification(
            t('admin:organizationSuccessfulUpdated', { id: fullOrganisation.recordId }),
        );
        onClose();
    };

    const errorRequest = () => {
        NotificationService.showNotification(t('errorMessage'));
        disabledSaveButton.setFalse();
    };

    const submit = () => {
        const newModulesNumber = selectedItems.reduce((sum, module) => sum + module.id, 0);
        disabledSaveButton.setTrue();
        saveOrganisationModules({
            variables: {
                organisationKey: currentOrganization.key,
                modules: newModulesNumber,
                reason: '',
            },
            refetchQueries: [
                { query: GET_SYSTEM_ORGANISATIONS },
                {
                    query: GET_ORGANISATION,
                    variables: {
                        key: currentOrganization.key,
                    },
                },
            ],
        })
            .then(() => {
                successRequest();
            })
            .catch(() => {
                errorRequest();
            });
    };

    return (
        <>
            {open && fullOrganisation && (
                <CustomDialog
                    onClose={() => {
                        onClose();
                    }}
                    fullWidth
                    maxWidth="md"
                    scroll="paper"
                    open={open}
                >
                    <DialogTitle id="assign-module-title">
                        {t('admin:assignModulesId', { id: fullOrganisation.recordId })}
                        <FontAwesomeIcon
                            icon={['fal', 'times']}
                            className="fa-lg close-icon-cv"
                            onClick={() => {
                                onClose();
                            }}
                            data-testid="close-dialog"
                        />
                    </DialogTitle>
                    <DialogContent className="assign-module-dialog-content pb-0">
                        <>
                            <div className="list-info">
                                <p className="text-length">
                                    <span className="bold">{t('admin:organization')}:</span>{' '}
                                    {` ${fullOrganisation?.name}`}
                                </p>
                                <p className="total-records">
                                    {t('admin:numberModuleRecords', { number: modulesList.length })}
                                </p>
                            </div>
                            <ItemList
                                id="assign-module-list"
                                items={modulesList}
                                textField="name"
                                multiselect
                                closed={!open}
                                selectedItems={selectedItems}
                                onSelectionChange={(value: any) => {
                                    disabledSaveButton.setFalse();
                                    setSelectedItems(value);
                                }}
                            />
                        </>
                    </DialogContent>
                    <DialogActions className="assign-module-dialog-content-actions flex-end flex-wrap pt-0">
                        <div className="selected-items-container mb-20">
                            <p>
                                {selectedItems.length}/{modulesList.length}
                            </p>
                        </div>
                        <Button
                            disabled={disabledSaveButton.value || !modulesList.length}
                            className="assign-module-btn"
                            text="update"
                            onClick={submit}
                        />
                    </DialogActions>
                </CustomDialog>
            )}
        </>
    );
};

export default AssignModulesToOrganisationDialog;
